import '../instrument';
import 'antd/dist/reset.css';
import '@/styles/styles.scss';
import '@ant-design/v5-patch-for-react-19';

import { px2remTransformer, StyleProvider } from '@ant-design/cssinjs';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App as AntApp, ConfigProvider } from 'antd';
import jaJP from 'antd/lib/locale/ja_JP';
import { Amplify } from 'aws-amplify';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { ErrorBoundary, RootFallback } from '@/components';
import { TIME_ZONE } from '@/constants/datetime.constants';
import { AuthRoutes } from '@/pages/auth/routes';
import { FeaturesRoutes } from '@/pages/routes';
import { theme } from '@/styles/antd-theme';

import awsConfig from '../aws-exports';
import App from './app';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      networkMode: 'offlineFirst',
    },
    mutations: { networkMode: 'offlineFirst' },
  },
});

const px2rem = px2remTransformer({ rootValue: 16 });
Amplify.configure(awsConfig);

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.tz.setDefault(TIME_ZONE);

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter(
  [
    {
      element: <App />,
      errorElement: <ErrorBoundary />,
      children: [...FeaturesRoutes, ...AuthRoutes],
    },
  ],
  { future: { v7_relativeSplatPath: true } },
);

const validateMessages = {
  required: '${label}を入力してください。',
  whitespace: '${label}を入力してください。',
  string: {
    max: '${label}は${max}文字以下で入力してください。',
    min: '${label}は${min}文字以上で入力してください。',
  },
};

const root = createRoot(document.querySelector('#root')!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <StrictMode>
    <StyleProvider transformers={[px2rem]}>
      <ConfigProvider
        theme={theme}
        locale={jaJP}
        pagination={{ showSizeChanger: false }}
        button={{ autoInsertSpace: false }}
        form={{ colon: false, validateMessages }}
        modal={{ centered: true }}
      >
        <AntApp
          notification={{ placement: 'top' }}
          message={{ top: 40, duration: 4.5 }}
        >
          <QueryClientProvider client={queryClient}>
            <RouterProvider
              router={router}
              future={{ v7_startTransition: true }}
              fallbackElement={<RootFallback />}
            />
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </AntApp>
      </ConfigProvider>
    </StyleProvider>
  </StrictMode>,
);
